import { ReactElement } from 'react'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { AgentBasics } from 'app/lib/aiAgentOverview'
import { useUser } from 'app/utils'

/**
 * The editable basics of the Details card: agent name, business name, address and the phones. The page's save bar saves them.
 */
export function AiAgentBasicsFields({
  draft,
  onChange,
  locationName,
}: {
  draft: AgentBasics
  onChange: (draft: AgentBasics) => void
  /** What the agent calls the practice while no business name is set. */
  locationName: string
}): ReactElement {
  const canEditPhone = useCheckPermission()(`settings:location`)
  const isSuper = useUser()?.type === `super`
  const field = (key: keyof AgentBasics) => ({
    value: draft[key],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...draft, [key]: event.target.value }),
  })

  return (
    <>
      <div className="flex flex-col gap-2">
        <Label htmlFor="agent-name">Agent name</Label>
        <Input
          className="shadow-none"
          id="agent-name"
          data-testid="agent-name"
          placeholder="e.g. Eliza"
          {...field(`agentName`)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="business-name">Business name</Label>
        <Input
          className="shadow-none"
          id="business-name"
          data-testid="agent-business-name"
          placeholder={locationName}
          {...field(`businessName`)}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="business-address">Address</Label>
        <Input
          className="shadow-none"
          id="business-address"
          data-testid="agent-business-address"
          placeholder="Street, city, state"
          {...field(`businessAddress`)}
        />
      </div>
      <div className="flex flex-col gap-2 md:col-start-1">
        <Label htmlFor="office-phone">Office phone</Label>
        <TooltipWrapper
          message={canEditPhone ? undefined : `Changing the office phone needs the Manage Location permission.`}
        >
          <Input
            className="shadow-none"
            id="office-phone"
            data-testid="agent-office-phone"
            type="tel"
            disabled={!canEditPhone}
            {...field(`officePhone`)}
          />
        </TooltipWrapper>
      </div>
      {isSuper && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="transfer-phone">Transfers calls to</Label>
          <Input
            className="shadow-none"
            id="transfer-phone"
            data-testid="agent-transfer-phone"
            type="tel"
            placeholder="Same as the office phone"
            {...field(`transferPhone`)}
          />
        </div>
      )}
    </>
  )
}
